import React, { useState, useEffect, useCallback } from "react";
import { McButton } from "@maersk-global/mds-react-wrapper/components-core";
import { ChainedFiveWhysService } from "../../services/chainedFiveWhysService";
import ChainTabs from "./ChainTabs";
import ChainContent from "./ChainContent";
import "./chainedFiveWhys.css";
import { useMsal } from "@azure/msal-react";
import { useQueryClient } from "react-query";

function ChainedFiveWhys({ incidentID, onExitEditMode, onClearAllChains }) {
  const { instance, accounts } = useMsal();
  const queryClient = useQueryClient();
  const service = ChainedFiveWhysService();

  const [chains, setChains] = useState([]);
  const [activeChainId, setActiveChainId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [currentIncidentID, setCurrentIncidentID] = useState(null);

  useEffect(() => {
    const loadChains = async () => {
      if (!incidentID) return;
      if (currentIncidentID === incidentID) return;

      setCurrentIncidentID(incidentID);
      setLoading(true);
      try {
        const fetchedChains = await service.getChainedFiveWhysCached(queryClient, incidentID, instance, accounts);
        if (fetchedChains && fetchedChains.length > 0) {
          setChains(fetchedChains);
          setActiveChainId(fetchedChains[0].chainId);
        } else {
          setChains([]);
          setActiveChainId(null);
        }
      } catch (error) {
        console.error('[ChainedFiveWhys] Error loading chains:', error);
      } finally {
        setLoading(false);
      }
    };
    loadChains();
  }, [incidentID]);

  const handleAddChain = () => {
    const nextLetter = service.getNextChainLetter(chains);
    const newChain = service.createEmptyChain(nextLetter);
    setChains([...chains, newChain]);
    setActiveChainId(newChain.chainId);
  };

  const handleDeleteChain = (chainId) => {
    const chainToDelete = chains.find(c => c.chainId === chainId);
    const confirmMessage = chainToDelete?.chainLabel
      ? `Delete Chain ${chainToDelete.chainLetter} — ${chainToDelete.chainLabel}?`
      : `Delete Chain ${chainToDelete?.chainLetter}?`;

    if (!window.confirm(confirmMessage)) return;

    // Re-number chain letters sequentially after deletion
    const updatedChains = chains
      .filter(c => c.chainId !== chainId)
      .map((chain, index) => ({ ...chain, chainLetter: String.fromCharCode(65 + index) }));

    setChains(updatedChains);
    if (activeChainId === chainId) {
      setActiveChainId(updatedChains.length > 0 ? updatedChains[0].chainId : null);
    }
  };

  const handleUpdateChain = useCallback((chainId, updates) => {
    setChains(prevChains => prevChains.map(chain =>
      chain.chainId === chainId ? { ...chain, ...updates } : chain
    ));
  }, []);

  const handleSaveAndExit = async () => {
    setSaving(true);
    try {
      if (chains.length === 0) {
        // API rejects empty array — notify parent to clear local state instead
        if (onClearAllChains) onClearAllChains();
        else if (onExitEditMode) onExitEditMode();
      } else {
        await service.saveChainedFiveWhys(incidentID, chains, instance, accounts);
        queryClient.invalidateQueries(["chainedFiveWhys", incidentID]);
        if (onExitEditMode) onExitEditMode();
      }
    } catch (error) {
      console.error('[ChainedFiveWhys] Error saving chains:', error);
      alert('Failed to save chains. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const activeChain = chains.find(c => c.chainId === activeChainId);

  // Overall progress across all chains
  const totalFilled = chains.reduce((sum, c) => sum + (c.whys?.filter(w => w.question?.trim() && w.answer?.trim()).length || 0), 0);
  const totalRequired = chains.length * 5;

  if (loading) {
    return (
      <div style={{ padding: "20px", textAlign: "center", color: "var(--mds_brand_appearance_default_default_text-color-secondary, #6b6b6b)" }}>
        Loading chains…
      </div>
    );
  }

  return (
    <div className="chained-five-whys">
      {/* Tabs */}
      {chains.length > 0 ? (
        <ChainTabs
          chains={chains}
          activeChainId={activeChainId}
          onSelectChain={setActiveChainId}
          onAddChain={handleAddChain}
          onDeleteChain={handleDeleteChain}
        />
      ) : (
        /* Empty state */
        <div style={{
          padding: "32px 20px",
          border: "2px dashed var(--mds_brand_appearance_default_border-color, #e0e0e0)",
          borderRadius: "var(--mds_brand_border_medium_radius, 4px)",
          textAlign: "center",
          background: "var(--mds_brand_appearance_neutral_weakest_background-color, #fafafa)",
        }}>
          <p style={{ fontSize: "14px", fontWeight: "600", color: "var(--mds_brand_appearance_default_default_text-color, #141414)", marginBottom: "6px" }}>
            No 5 Whys chains yet
          </p>
          <p style={{ fontSize: "12px", color: "var(--mds_brand_appearance_default_default_text-color-secondary, #6b6b6b)", marginBottom: "20px" }}>
            Create a chain and drill down through 5 levels of "Why?" to find the root cause
          </p>
          <McButton appearance="primary" fit="small" trailingicon="plus" click={handleAddChain}>
            Create Chain A
          </McButton>
        </div>
      )}

      {/* Progress hint (shown once a chain has at least one why started) */}
      {chains.length > 0 && totalRequired > 0 && (
        <div style={{
          display: "flex",
          alignItems: "center",
          gap: "10px",
          padding: "8px 0",
          marginBottom: "4px",
        }}>
          <div style={{ flex: 1, height: "4px", background: "var(--mds_brand_appearance_default_border-color, #e0e0e0)", borderRadius: "2px", overflow: "hidden" }}>
            <div style={{
              height: "100%",
              width: `${Math.min(100, (totalFilled / totalRequired) * 100)}%`,
              background: totalFilled === totalRequired
                ? "var(--mds_brand_appearance_success_default_background-color, #40ab35)"
                : "var(--mds_brand_appearance_primary_default_background-color, #0073ab)",
              borderRadius: "2px",
              transition: "width 0.3s ease",
            }} />
          </div>
          <span style={{
            fontSize: "11px", fontWeight: "700", whiteSpace: "nowrap",
            color: totalFilled === totalRequired
              ? "var(--mds_brand_appearance_success_default_text-color, #40ab35)"
              : "var(--mds_brand_appearance_default_default_text-color-secondary, #6b6b6b)",
          }}>
            {totalFilled}/{totalRequired} Whys filled
          </span>
        </div>
      )}

      {/* Active chain editor */}
      {chains.length > 0 && activeChain && (
        <ChainContent
          key={activeChainId}
          chain={activeChain}
          onUpdateChain={handleUpdateChain}
        />
      )}

      {/* Save & Exit — always visible */}
      <div style={{ padding: "12px 0 4px", display: "flex", justifyContent: "flex-end", gap: "8px", marginTop: "8px", borderTop: "1px solid var(--mds_brand_appearance_default_border-color, #e0e0e0)" }}>
        <McButton
          appearance="primary"
          fit="small"
          disabled={saving}
          click={handleSaveAndExit}
        >
          {saving ? "Saving…" : "Save & Exit"}
        </McButton>
      </div>
    </div>
  );
}

export default ChainedFiveWhys;
